import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { $ } from '../utils/dom';
import {
  escapeHtml,
  machineStatusBadgeClass,
  machineStatusLabel,
  MACHINE_STATUS_OPTIONS,
  type MachineStatusApi,
} from '../utils/catalog-labels';
import { renderConfirmModal, bindModal, showModal } from '../utils/ui';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface Machine {
  id: string;
  name: string;
  code: string;
  status: MachineStatusApi;
  createdAt: string;
}

type FormMode = 'none' | 'create' | 'edit';

export class AdminMachinesPage implements View {
  private machines: Machine[] = [];
  private loading = true;
  private errorMsg: string | null = null;
  private statusFilter: MachineStatusApi | 'all' = 'all';
  private search = '';
  private formMode: FormMode = 'none';
  private editingId: string | null = null;
  private deleteMachineId: string | null = null;

  async render(): Promise<string> {
    this.readParams();
    await this.loadData();

    if (this.loading) {
      return `<div class="admin-state-loading" data-testid="admin-machines-loading"><p>Cargando parque de máquinas...</p></div>`;
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass admin-state-error" data-testid="admin-machines-error">
          <h3>Error al cargar máquinas</h3>
          <p>${escapeHtml(this.errorMsg)}</p>
          <button class="btn btn-primary" onclick="window.location.reload()">Reintentar</button>
        </div>
      `;
    }

    const filtered = this.getFiltered();
    const editing = this.formMode === 'edit' ? this.machines.find((m) => m.id === this.editingId) : null;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <nav class="admin-breadcrumb">
            <span>Parque de máquinas</span>
          </nav>
          <h2 data-testid="admin-machines-title">Parque de máquinas</h2>
          <p>Inventario operativo del gimnasio para ejercicios y rutinas.</p>
        </div>
        <button type="button" class="btn btn-primary" id="btn-new-machine" data-testid="btn-new-machine" style="display:inline-flex;align-items:center;gap:0.4rem;">
          ${getTyrisIcon('plus', { size: 16 })} Nueva máquina
        </button>
      </div>

      <div class="admin-api-notice card-glass" data-testid="machine-api-notice">
        <strong>Campos pendientes en API:</strong> descripción y sector no existen en el modelo <code>Machine</code>.
        Se gestionan <em>nombre</em>, <em>código</em> y <em>estado</em>.
      </div>

      ${this.formMode !== 'none' ? this.renderForm(editing) : ''}

      <div class="admin-filters card-glass" data-testid="admin-machines-filters">
        <div class="admin-filter-row">
          <label class="admin-filter-label" for="machine-search">Buscar</label>
          <input id="machine-search" type="search" class="admin-input" placeholder="Nombre o código..."
            value="${escapeHtml(this.search)}" data-testid="machine-search-input" />
        </div>
        <div class="admin-filter-row">
          <label class="admin-filter-label" for="machine-status-filter">Estado</label>
          <select id="machine-status-filter" class="admin-select" data-testid="machine-status-filter">
            <option value="all" ${this.statusFilter === 'all' ? 'selected' : ''}>Todos</option>
            ${MACHINE_STATUS_OPTIONS.map(
              (o) => `
              <option value="${o.value}" ${this.statusFilter === o.value ? 'selected' : ''}>${o.label}</option>
            `,
            ).join('')}
          </select>
        </div>
      </div>

      <div class="card-glass admin-table-wrap" data-testid="admin-machines-table">
        ${
          filtered.length === 0
            ? `<div class="admin-empty" data-testid="admin-machines-empty"><p>No hay máquinas con estos filtros.</p></div>`
            : `
          <table class="admin-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Código</th>
                <th>Descripción</th>
                <th>Sector</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.map((m) => this.renderRow(m)).join('')}
            </tbody>
          </table>
        `
        }
      </div>

      ${renderConfirmModal({
        id: 'confirm-delete-machine',
        title: '¿Eliminar máquina?',
        body: 'Esta acción no se puede deshacer y desvinculará esta máquina de cualquier ejercicio en el catálogo.',
        confirmLabel: 'Eliminar',
        danger: true,
      })}
    `;
  }

  afterRender(): void {
    $('#btn-new-machine')?.addEventListener('click', () => {
      window.location.hash = '#/admin/machines?form=create';
    });

    $('#btn-cancel-machine-form')?.addEventListener('click', () => {
      window.location.hash = '#/admin/machines';
    });

    const searchInput = document.getElementById('machine-search') as HTMLInputElement | null;
    const statusSelect = document.getElementById('machine-status-filter') as HTMLSelectElement | null;

    const applyFilters = () => {
      const parts = new URLSearchParams();
      const q = searchInput?.value.trim();
      const st = statusSelect?.value ?? 'all';
      if (q) parts.set('q', q);
      if (st !== 'all') parts.set('status', st);
      if (this.formMode !== 'none') {
        parts.set('form', this.formMode);
        if (this.editingId) parts.set('id', this.editingId);
      }
      const query = parts.toString();
      window.location.hash = query ? `#/admin/machines?${query}` : '#/admin/machines';
    };

    let debounce: ReturnType<typeof setTimeout> | undefined;
    searchInput?.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(applyFilters, 300);
    });
    statusSelect?.addEventListener('change', applyFilters);

    $('#form-machine')?.addEventListener('submit', (e) => this.handleSubmit(e));

    document.querySelectorAll('[data-machine-edit]').forEach((el) => {
      el.addEventListener('click', () => {
        const id = (el as HTMLElement).getAttribute('data-machine-edit');
        if (id) window.location.hash = `#/admin/machines?form=edit&id=${id}`;
      });
    });

    document.querySelectorAll('[data-machine-delete]').forEach((el) => {
      el.addEventListener('click', () => {
        const id = (el as HTMLElement).getAttribute('data-machine-delete');
        if (id) {
          this.deleteMachineId = id;
          showModal('confirm-delete-machine');
        }
      });
    });

    bindModal({
      modalId: 'confirm-delete-machine',
      onConfirm: async () => {
        if (this.deleteMachineId) {
          await this.executeDelete(this.deleteMachineId);
        }
      },
    });
  }

  private renderRow(m: Machine): string {
    return `
      <tr data-testid="machine-row-${m.id}">
        <td><span class="admin-cell-primary">${escapeHtml(m.name)}</span></td>
        <td><code style="color:var(--color-secondary);">${escapeHtml(m.code)}</code></td>
        <td class="admin-cell-muted" title="No disponible en API">—</td>
        <td class="admin-cell-muted" title="No disponible en API">—</td>
        <td>
          <span class="${machineStatusBadgeClass(m.status)}" data-testid="machine-badge-${m.id}">
            ${machineStatusLabel(m.status)}
          </span>
        </td>
        <td class="admin-row-actions">
          <button type="button" class="btn btn-outline btn-sm" data-machine-edit="${m.id}" data-testid="machine-edit-${m.id}" style="display:inline-flex;align-items:center;gap:0.25rem;">${getTyrisIcon('editar', { size: 14 })}</button>
          <button type="button" class="btn btn-outline btn-sm" data-machine-delete="${m.id}" data-testid="machine-delete-${m.id}"
            style="border-color:rgba(220,53,69,0.35);color:hsl(350,80%,65%);display:inline-flex;align-items:center;gap:0.25rem;">${getTyrisIcon('darse-de-baja', { size: 14 })}</button>
        </td>
      </tr>
    `;
  }

  private renderForm(editing: Machine | null | undefined): string {
    const title = this.formMode === 'create' ? 'Nueva máquina' : 'Editar máquina';
    const name = editing?.name ?? '';
    const code = editing?.code ?? '';
    const status = editing?.status ?? 'USABLE';
    const codeReadonly = this.formMode === 'edit' ? 'readonly style="opacity:0.7;"' : '';

    return `
      <div class="card-glass admin-form-card" data-testid="admin-machine-form">
        <h3 class="admin-section-title">${title}</h3>
        <form id="form-machine" novalidate>
          <div class="admin-form-grid">
            <label>Nombre *
              <input name="name" class="admin-input" required value="${escapeHtml(name)}" data-testid="machine-form-name" />
            </label>
            <label>Código / identificador *
              <input name="code" class="admin-input" required ${codeReadonly} value="${escapeHtml(code)}"
                data-testid="machine-form-code" placeholder="M-EJ-01" />
            </label>
            <label>Estado *
              <select name="status" class="admin-select" data-testid="machine-form-status">
                ${MACHINE_STATUS_OPTIONS.map(
                  (o) => `
                  <option value="${o.value}" ${status === o.value ? 'selected' : ''}>${o.label}</option>
                `,
                ).join('')}
              </select>
            </label>
          </div>
          <p class="admin-cell-muted" style="margin-top:0.75rem;font-size:0.85rem;">
            Descripción y sector se habilitarán cuando el backend los exponga en el modelo.
          </p>
          <div class="admin-form-actions">
            <button type="submit" class="btn btn-primary" data-testid="machine-form-submit">Guardar</button>
            <button type="button" class="btn btn-outline" id="btn-cancel-machine-form">Cancelar</button>
          </div>
        </form>
      </div>
    `;
  }

  private readParams(): void {
    const params = router.getParams();
    this.search = (params.q ?? '').toLowerCase();
    if (params.status && (params.status === 'all' || MACHINE_STATUS_OPTIONS.some((o) => o.value === params.status))) {
      this.statusFilter = params.status as MachineStatusApi | 'all';
    }
    if (params.form === 'create') {
      this.formMode = 'create';
      this.editingId = null;
    } else if (params.form === 'edit' && params.id) {
      this.formMode = 'edit';
      this.editingId = params.id;
    } else {
      this.formMode = 'none';
      this.editingId = null;
    }
  }

  private getFiltered(): Machine[] {
    return this.machines.filter((m) => {
      if (this.statusFilter !== 'all' && m.status !== this.statusFilter) return false;
      if (this.search) {
        const hay = `${m.name} ${m.code}`.toLowerCase();
        if (!hay.includes(this.search)) return false;
      }
      return true;
    });
  }

  private async loadData(): Promise<void> {
    try {
      this.machines = await apiClient.get<Machine[]>('/api/machines');
      this.errorMsg = null;
    } catch (err: unknown) {
      this.errorMsg = err instanceof Error ? err.message : 'Error al cargar máquinas';
    } finally {
      this.loading = false;
    }
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    const name = String(fd.get('name') ?? '').trim();
    const code = String(fd.get('code') ?? '').trim().toUpperCase();
    const status = String(fd.get('status') ?? 'USABLE') as MachineStatusApi;

    if (!name || !code) {
      toast.error('Nombre y código son obligatorios.');
      return;
    }

    try {
      if (this.formMode === 'edit' && this.editingId) {
        await apiClient.patch(`/api/machines/${this.editingId}`, { name, status });
        toast.success('Máquina actualizada.');
      } else {
        await apiClient.post('/api/machines', { name, code, status });
        toast.success('Máquina creada.');
      }
      window.location.hash = '#/admin/machines';
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const msg = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
      toast.error(msg ?? 'No se pudo guardar la máquina.');
    }
  }

  private async executeDelete(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/machines/${id}`);
      toast.success('Máquina eliminada.');
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string } })?.body;
      toast.error(body?.message ?? 'No se pudo eliminar la máquina.');
    } finally {
      this.deleteMachineId = null;
    }
  }
}
